import { batch } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { DEFAULT_FONT_FAMILY } from '@ff-os/pdp-shared-redux/apis/fonts/loadFonts';
import { Font, FontStyle, selectFamilySupportsBoldByName, selectFamilySupportsItalicByName } from '@ff-os/pdp-shared-redux/slices/fontsSlice';
import { createInputBindings } from '@ff-scene-plugins/gift-redux/slices/inputsSlice';
import { createOptionBindings } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpState, GiftPdpThunk } from '@ff-scene-plugins/gift-redux/store';
import { TextVariableInput } from '@gift-variable-plugins/text-shared/TextVariableInput';
import { TextVariableOptions } from '@gift-variable-plugins/text-shared/TextVariableOptions';
import { withFontMetricsUpdate } from './withFontMetricsUpdate';

const { createInputSelector, createInputUpdater } = createInputBindings<TextVariableInput>();

export const selectContentById = createInputSelector(i => i.content);
export const updateContentByIds = withFontMetricsUpdate(createInputUpdater('content'));

const selectFontFamilyFromInputById = createInputSelector(i => i.fontFamily);

export const selectFontFamilyNameById = createSelector([
    selectFontFamilyFromInputById,
    (state: GiftPdpState) => state.fonts.families
], (selectedFontFamilyName, fontFamilies) => {
    if (fontFamilies.map(f => f.name).includes(selectedFontFamilyName!)) {
        return selectedFontFamilyName;
    } else {
        return DEFAULT_FONT_FAMILY.name;
    }
});

export const selectFontFamilyById = createSelector([
    selectFontFamilyNameById,
    (state: GiftPdpState) => state.fonts.families
], (fontFamily, fontFamilies) => {
    return fontFamilies.find(f => f.name === fontFamily) || DEFAULT_FONT_FAMILY;
});

export const selectFontsById = createSelector([
    selectFontFamilyById
], family => family?.fonts ?? [] as Font[]);

export const selectHasFontFamilyStyle = (style: FontStyle) => createSelector(
    selectFontsById,
    fonts => fonts.some(font => font.style === style));

export const selectHasNonRegularStyleById = createSelector(
    selectFontsById,
    fonts => fonts.some(font => font.style !== 'regular'));

export const selectHasFontFamilyBold = selectHasFontFamilyStyle('bold');
export const selectHasFontFamilyItalic = selectHasFontFamilyStyle('italic');
export const selectHasFontFamilyBoldItalic = selectHasFontFamilyStyle('bolditalic');

export const selectFontWeightById = createInputSelector(i => i.fontWeight);
export const updateFontWeightByIds = withFontMetricsUpdate(createInputUpdater('fontWeight'));

export const selectFontStyleById = createInputSelector(i => i.fontStyle);
export const updateFontStyleByIds = withFontMetricsUpdate(createInputUpdater('fontStyle'));

export const selectCanUpdateFontWeightById = createSelector([
    selectHasFontFamilyBoldItalic,
    selectFontStyleById
], (hasBoldItalic, fontStyle) => {
    return fontStyle === 'normal' || (fontStyle === 'italic' && hasBoldItalic);
});

export const selectCanUpdateFontStyleById = createSelector([
    selectHasFontFamilyBoldItalic,
    selectFontWeightById
], (hasBoldItalic, fontWeight) => {
    return fontWeight === 'normal' || (fontWeight === 'bold' && hasBoldItalic);
});

export const selectComputedHeightById = createInputSelector(i => i.computedHeight);
export const updateComputedHeightByIds = createInputUpdater('computedHeight');

export const selectComputedFontSizeById = createInputSelector(i => i.computedFontSize);
export const updateComputedFontSizeByIds = createInputUpdater('computedFontSize');

const updateFontFamily = withFontMetricsUpdate(createInputUpdater('fontFamily'));

export const updateFontFamilyByIds = (variableIds: string[], targetFamily: string): GiftPdpThunk => {

    return (dispatch, getState) => {
        batch(() => {

            const state = getState();

            if (!selectFamilySupportsBoldByName(state, targetFamily)) {
                dispatch(updateFontWeightByIds(variableIds, 'normal'));
            }

            if (!selectFamilySupportsItalicByName(state, targetFamily)) {
                dispatch(updateFontStyleByIds(variableIds, 'normal'));
            }

            dispatch(updateFontFamily(variableIds, targetFamily));
        });
    };
};

export const selectInputById = createInputSelector(i => i);

const { createOptionSelector } = createOptionBindings<TextVariableOptions>();

export const selectMaxLengthById = createOptionSelector(o => o.maxLength);